import moment from "moment"
import { sanitizeAmount } from "../../../helpers/sanitizeAmount"
import { FILTER_DATE_FORMAT } from "../../../helpers/dateHandlers"
import { teamSize } from "../../../helpers/helpData"
import {
  eligibilityOptions,
  businessFunctionOptions,
  geographicEligibilityOptions,
  optionLabel,
} from "../../Opportunity/criteriaOptions"
import {
  experienceLevelOptions,
  workEnvironmentOptions,
  fundingTypeOptions,
} from "../../Opportunity/dropdownOptions"
import { options as industryOptions } from "../MemberSearchFilter/wrappers/CompanyIndustryTypeDropdown/options"
import { options as levelTypeOptions } from "../MemberSearchFilter/wrappers/LevelTypeDropdown/options"
import { options as sectorOptions } from "../MemberSearchFilter/wrappers/CompanyRoleTypeDropdown/options"
import { opportunities as opportunityTypeOptions } from "./wrappers/TypeDropdown/opportunities"

// Core and Advanced filters are mutually exclusive: only the active mode's
// values are ever sent to the API.
export const CORE = "core"
export const ADVANCED = "advanced"
export const MODE_PARAM = "filter_mode"
export const DEFAULT_STATUS = "open"
export const CUSTOM_DEADLINE = "custom"
const DATE_FORMAT = "YYYY-MM-DD"

const withAll = (label, options) => [
  { id: "", value: label },
  ...options.filter((option) => option.id !== ""),
]

// Existing opportunity dropdowns store their label, so the label is also the filter value.
const byLabel = (options) => options.map(({ value }) => ({ id: value, value }))

export const filterOptions = {
  // New fields (no existing equivalent)
  eligibility: withAll("All Eligible Applicants", eligibilityOptions),
  businessFunctions: businessFunctionOptions,
  geographicEligibility: withAll("All Regions", geographicEligibilityOptions),
  source: withAll("All Sources", [
    { id: "b2t_admin", value: "B2T Platform Administrator" },
    { id: "company", value: "Named Company/Organization" },
  ]),
  deadline: [
    { id: "", value: "Any Time" },
    { id: "30", value: "Next 30 Days" },
    { id: "60", value: "Next 60 Days" },
    { id: "90", value: "Next 90 Days" },
    { id: CUSTOM_DEADLINE, value: "Custom Date Range" },
  ],
  // Existing values reused as they are
  industry: withAll("All Industries", industryOptions),
  organizationType: sectorOptions.filter((option) => option.id !== ""),
  levelType: withAll("All Stages", levelTypeOptions),
  companySize: withAll(
    "All Sizes",
    teamSize.map(({ dataName, value }) => ({ id: dataName, value })),
  ),
  experienceLevel: withAll("All Levels", byLabel(experienceLevelOptions)),
  workEnvironment: withAll("Any", byLabel(workEnvironmentOptions)),
  // Same "Type of funding" options as the create form.
  fundingTypes: byLabel(fundingTypeOptions),
  status: [
    { id: "open", value: "Open" },
    { id: "closed", value: "Closed" },
  ],
}

// `kind`: "text" (default), "list" (comma separated) or "date" (moment <-> YYYY-MM-DD).
// `options` is used to label the active filter tags.
export const FILTER_FIELDS = {
  [CORE]: [
    {
      name: "opportunityType",
      param: "opportunity_type",
      options: opportunityTypeOptions.map(({ type, title }) => ({
        id: type,
        value: title,
      })),
    },
    {
      name: "eligibility",
      param: "eligibility",
      options: filterOptions.eligibility,
    },
    { name: "industry", param: "industry", options: filterOptions.industry },
    { name: "location", param: "location" },
    { name: "fromDate", param: "from", kind: "date", tag: "From" },
    { name: "toDate", param: "to", kind: "date", tag: "To" },
    { name: "valueMin", param: "value_min", tag: "Min value" },
    { name: "valueMax", param: "value_max", tag: "Max value" },
    { name: "keywords", param: "keywords", tag: "Keywords" },
    {
      name: "deadline",
      param: "deadline",
      options: filterOptions.deadline,
      tag: "Deadline",
    },
    {
      name: "deadlineFrom",
      param: "deadline_from",
      kind: "date",
      tag: "Deadline from",
    },
    {
      name: "deadlineTo",
      param: "deadline_to",
      kind: "date",
      tag: "Deadline to",
    },
  ],
  [ADVANCED]: [
    {
      name: "organizationType",
      param: "describe_company",
      kind: "list",
      options: filterOptions.organizationType,
    },
    {
      name: "levelType",
      param: "level_type",
      options: filterOptions.levelType,
    },
    { name: "commitment", param: "commitment", tag: "Commitment" },
    {
      name: "experienceLevel",
      param: "experience_level",
      options: filterOptions.experienceLevel,
      tag: "Experience",
    },
    { name: "certifications", param: "certifications", tag: "Certification" },
    {
      name: "companySize",
      param: "team_size",
      options: filterOptions.companySize,
    },
    {
      name: "fundingTypes",
      param: "type_of_funding",
      kind: "list",
      options: filterOptions.fundingTypes,
    },
    {
      name: "workEnvironment",
      param: "work_environment",
      options: filterOptions.workEnvironment,
      tag: "Work model",
    },
    { name: "duration", param: "duration", tag: "Duration" },
    {
      name: "businessFunctions",
      param: "business_functions",
      kind: "list",
      options: filterOptions.businessFunctions,
    },
    {
      name: "geographicEligibility",
      param: "geographic_eligibility",
      options: filterOptions.geographicEligibility,
    },
    {
      name: "status",
      param: "status",
      options: filterOptions.status,
      tag: "Status",
    },
    {
      name: "source",
      param: "source",
      options: filterOptions.source,
      tag: "Source",
    },
  ],
}

const COMPANY_PARAMS = ["company", "company_name"]

export const MODE_PARAMS = {
  [CORE]: [...FILTER_FIELDS[CORE].map(({ param }) => param), ...COMPANY_PARAMS],
  [ADVANCED]: FILTER_FIELDS[ADVANCED].map(({ param }) => param),
}

export const ALL_FILTER_PARAMS = [
  ...MODE_PARAMS[CORE],
  ...MODE_PARAMS[ADVANCED],
  MODE_PARAM,
]

// Params that depend on another one and are removed together with it.
const DEPENDENT_PARAMS = {
  company: ["company_name"],
  deadline: ["deadline_from", "deadline_to"],
}

const emptyValue = ({ kind }) => {
  if (kind === "list") return []
  if (kind === "date") return null
  return ""
}

export const initialValues = {
  [CORE]: {
    ...Object.fromEntries(
      FILTER_FIELDS[CORE].map((field) => [field.name, emptyValue(field)]),
    ),
    company: null,
    companyInput: "",
    locationInput: "",
  },
  [ADVANCED]: {
    ...Object.fromEntries(
      FILTER_FIELDS[ADVANCED].map((field) => [field.name, emptyValue(field)]),
    ),
    status: DEFAULT_STATUS,
  },
}

export const allInitialValues = {
  ...initialValues[CORE],
  ...initialValues[ADVANCED],
}

const hasValue = (value) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== ""

const toParam = ({ kind }, value) => {
  if (kind === "list") return value.join(",")
  if (kind === "date") return moment(value).format(DATE_FORMAT)
  return String(value).trim()
}

const fromParam = ({ kind }, value) => {
  if (kind === "list") return String(value).split(",").filter(Boolean)
  if (kind === "date") return moment(value, DATE_FORMAT)
  return value
}

// "5." and "." are fine while typing but not as a search value; the API accepts up to 13 digits.
const toAmountParam = (value) => {
  const amount = sanitizeAmount(value).replace(/\.$/, "")
  return /^\d{1,13}(\.\d{1,2})?$/.test(amount) ? amount : ""
}

/** Panel values of the active mode -> URL/API search params. */
export const toSearchParams = (mode, values) => {
  const params = {}
  FILTER_FIELDS[mode].forEach((field) => {
    const value = values[field.name]
    if (!hasValue(value)) return
    if (field.param === "status" && value === DEFAULT_STATUS) return
    if (
      ["deadline_from", "deadline_to"].includes(field.param) &&
      values.deadline !== CUSTOM_DEADLINE
    )
      return
    const param = field.param.startsWith("value_")
      ? toAmountParam(value)
      : toParam(field, value)
    if (param === "") return
    params[field.param] = param
  })
  if (mode === CORE && values.company) {
    params.company = values.company.id
    params.company_name = values.company.name
  }
  if (mode === ADVANCED) params[MODE_PARAM] = ADVANCED
  return params
}

/** Number of filters applied in the panel; every list item and every free text value counts once. */
export const countAppliedFilters = (mode, values) => {
  const params = toSearchParams(mode, values)
  return (
    FILTER_FIELDS[mode].reduce((count, { param, kind }) => {
      if (!(param in params) || param.startsWith("deadline_")) return count
      return count + (kind === "list" ? params[param].split(",").length : 1)
    }, 0) + (params.company ? 1 : 0)
  )
}

const hasModeParams = (mode, searchParamGroup) =>
  MODE_PARAMS[mode].some((param) => hasValue(searchParamGroup[param]))

/** URL search params -> {mode, values}. `mode` is null when no filter is applied. */
export const fromSearchParams = (searchParamGroup = {}) => {
  let mode = null
  if (searchParamGroup[MODE_PARAM] === ADVANCED) mode = ADVANCED
  else if (hasModeParams(CORE, searchParamGroup)) mode = CORE

  const values = { ...allInitialValues }
  ;[...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].forEach((field) => {
    const value = searchParamGroup[field.param]
    if (hasValue(value)) values[field.name] = fromParam(field, value)
  })
  if (searchParamGroup.company) {
    values.company = {
      id: searchParamGroup.company,
      name: searchParamGroup.company_name,
    }
    values.companyInput = searchParamGroup.company_name || ""
  }
  values.locationInput = values.location
  return { mode, values }
}

export const isFilterApplied = (searchParamGroup = {}) =>
  ALL_FILTER_PARAMS.filter((param) => param !== MODE_PARAM).some((param) =>
    hasValue(searchParamGroup[param]),
  )

export const omitFilterParams = (
  searchParamGroup,
  params = ALL_FILTER_PARAMS,
) =>
  Object.fromEntries(
    Object.entries(searchParamGroup).filter(([key]) => !params.includes(key)),
  )

/** Removes one applied filter (or one value of a list filter) from the search params. */
export const removeFilterParam = (searchParamGroup, param, value) => {
  const field = [...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].find(
    (item) => item.param === param,
  )
  if (field?.kind === "list" && value) {
    const remaining = fromParam(field, searchParamGroup[param]).filter(
      (item) => item !== value,
    )
    if (remaining.length)
      return { ...searchParamGroup, [param]: remaining.join(",") }
  }
  const remaining = omitFilterParams(searchParamGroup, [
    param,
    ...(DEPENDENT_PARAMS[param] || []),
  ])
  const isEmptyCustomDeadline =
    remaining.deadline === CUSTOM_DEADLINE &&
    !remaining.deadline_from &&
    !remaining.deadline_to
  return isEmptyCustomDeadline
    ? omitFilterParams(remaining, ["deadline"])
    : remaining
}

const formatTagValue = (field, value) => {
  if (field.kind === "date")
    return moment(value, DATE_FORMAT).format(FILTER_DATE_FORMAT)
  if (field.param.startsWith("value_"))
    return `$${Number(value).toLocaleString("en-US")}`
  return optionLabel(field.options || [], value) || value
}

/** Active filter tags shown above the results: one tag per value. */
export const buildFilterTags = (searchParamGroup = {}) => {
  const tags = []
  ;[...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].forEach((field) => {
    const raw = searchParamGroup[field.param]
    if (!hasValue(raw)) return
    if (field.param === "deadline" && raw === CUSTOM_DEADLINE) return
    const values = field.kind === "list" ? fromParam(field, raw) : [raw]
    values.forEach((value) => {
      const label = formatTagValue(field, value)
      tags.push({
        key: `${field.param}-${value}`,
        param: field.param,
        value: field.kind === "list" ? value : undefined,
        title: field.tag ? `${field.tag}: ${label}` : label,
      })
    })
  })
  if (hasValue(searchParamGroup.company_name)) {
    tags.push({
      key: "company",
      param: "company",
      title: searchParamGroup.company_name,
    })
  }
  return tags
}
